import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Users, Mail, TrendingUp, Building, CheckCircle, Award, Shield } from 'lucide-react';

interface LiveStat {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  increment: number;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatar?: string;
  verified?: boolean;
}

const LiveSocialProof: React.FC = () => {
  const [liveStats, setLiveStats] = useState<LiveStat[]>([
    { label: 'Emails Sent Today', value: 47823, increment: 12 },
    { label: 'Active Campaigns', value: 1247, increment: 3 },
    { label: 'Meetings Booked', value: 892, increment: 2 },
    { label: 'Users Online', value: 156, increment: 1 }
  ]);

  const testimonials: Testimonial[] = [
    {
      name: "Sarah Chen",
      role: "VP of Sales",
      company: "TechFlow Solutions",
      content: "Munger AI transformed our outreach. We went from 2% to 8% reply rates and booked 3x more meetings. The AI writes better emails than our entire sales team.",
      rating: 5,
      verified: true
    },
    {
      name: "Marcus Rodriguez",
      role: "Head of Growth",
      company: "ScaleUp Labs",
      content: "The ROI was immediate. We saved 15 hours per week on email outreach and our conversion rate doubled. This is a game-changer for B2B sales.",
      rating: 5,
      verified: true
    },
    {
      name: "Emily Watson",
      role: "Sales Director",
      company: "DataDrive Corp",
      content: "I was skeptical about AI-generated emails, but Munger's personalization is incredible. Our prospects actually reply asking how we knew so much about them.",
      rating: 5,
      verified: true
    },
    {
      name: "David Park",
      role: "CEO",
      company: "GrowthHack Inc",
      content: "We went from sending 50 emails per day to 500, with better results. Munger AI scales personalization in a way that's impossible manually.",
      rating: 5,
      verified: true
    }
  ];

  // Update live stats periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveStats(prev => prev.map(stat => ({
        ...stat,
        value: stat.value + Math.floor(Math.random() * stat.increment) + 1
      })));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/5 via-purple-900/5 to-green-900/5" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Live Stats Ticker */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-gray-800/60 to-gray-900/60 rounded-2xl border border-gray-700/50 p-6 mb-16 shadow-2xl"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-green-400">Live Activity</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {liveStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <motion.div
                  key={stat.value}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-2xl md:text-3xl font-bold text-white mb-1"
                >
                  {stat.prefix}{stat.value.toLocaleString()}{stat.suffix}
                </motion.div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 mb-6">
            <Star className="w-5 h-5 text-yellow-400 fill-current" />
            <span className="text-sm font-semibold text-yellow-400">4.9/5 Average Rating</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Loved by Sales Teams
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            See what our customers are saying about their results
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-gray-700/50 p-8 shadow-2xl group hover:scale-105 transition-transform duration-300"
            >
              {/* Rating Stars */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              
              {/* Quote */}
              <blockquote className="text-gray-300 mb-6 leading-relaxed">
                "{testimonial.content}"
              </blockquote>
              
              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                  {testimonial.name.split(' ').map(n => n[0]).join('')}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">{testimonial.name}</span>
                    {testimonial.verified && (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    {testimonial.role} at {testimonial.company}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Security & Compliance */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 p-8 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-6">
            Enterprise-Grade Security
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mb-3 border border-green-500/30">
                <Shield className="w-8 h-8 text-green-400" />
              </div>
              <div className="text-sm font-semibold text-white mb-1">SOC 2 Compliant</div>
              <div className="text-xs text-gray-400">Type II Certified</div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-3 border border-blue-500/30">
                <Award className="w-8 h-8 text-blue-400" />
              </div>
              <div className="text-sm font-semibold text-white mb-1">GDPR Ready</div>
              <div className="text-xs text-gray-400">Data Protection</div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mb-3 border border-purple-500/30">
                <CheckCircle className="w-8 h-8 text-purple-400" />
              </div>
              <div className="text-sm font-semibold text-white mb-1">DMARC Passed</div>
              <div className="text-xs text-gray-400">Email Authentication</div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center mb-3 border border-orange-500/30">
                <TrendingUp className="w-8 h-8 text-orange-400" />
              </div>
              <div className="text-sm font-semibold text-white mb-1">Inbox Warm-up</div>
              <div className="text-xs text-gray-400">Built-in Reputation</div>
            </div>
          </div>
        </motion.div>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-4xl font-bold text-blue-400 mb-2">10M+</div>
              <div className="text-gray-300">Emails Sent</div>
            </div>
            
            <div>
              <div className="text-4xl font-bold text-purple-400 mb-2">2,500+</div>
              <div className="text-gray-300">Active Users</div>
            </div>
            
            <div>
              <div className="text-4xl font-bold text-green-400 mb-2">47%</div>
              <div className="text-gray-300">Avg. Open Rate</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveSocialProof; 